import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import React, { useState } from 'react';
import type BackendService from '../../../services/admin/BackendService';
import type {
  VideoAsset,
  VideoMetadata,
} from '../../../services/admin/adminBackendServiceVideoLibraryMixin';
import { useEditorSidebar } from './editorSidebarContext';
import MediaLibraryVideoItem from './MediaLibraryVideoItem';
import VideoLibrarySearchField from './VideoLibrarySearchField';
import VideoMetadataEditDialog from './VideoMetadataEditDialog';
import MediaUploadZone from './MediaUploadZone';
import { useVideoDeleteConfirm } from './useVideoDeleteConfirm';
import { useVideoLibrary } from './useVideoLibrary';

interface MediaLibraryTabProps {
  fundOpportunityId: string;
  backendService: BackendService;
  isNarrow: boolean;
}

const usageCount = (video: VideoAsset) => video.referenceCount ?? 0;

/**
 * A ready video can be deleted from here only when nothing links to it. A failed
 * one always can: it can't play, so whatever still points at it is already broken.
 */
const isDeletable = (video: VideoAsset) =>
  video.status === 'FAILED' || (video.status === 'READY' && video.referenceCount === 0);

function deleteMessage(video: VideoAsset): string {
  const uses = usageCount(video);
  if (uses === 0) return 'Delete this video from the library? This can’t be undone.';
  return `This video failed to process but is still linked in ${uses} place${uses === 1 ? '' : 's'}, which can’t play it. Delete it anyway?`;
}

function VideoList({
  videos,
  loading,
  isNarrow,
  onRemove,
  onEdit,
}: {
  videos: VideoAsset[];
  loading: boolean;
  isNarrow: boolean;
  onRemove: (video: VideoAsset) => Promise<void>;
  onEdit: (video: VideoAsset) => void;
}) {
  const { confirmId, setConfirmId, deleting, deleteError, cancelDelete, confirmDelete } =
    useVideoDeleteConfirm(onRemove);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }
  if (videos.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No videos yet — drop one above to add it.
      </Typography>
    );
  }
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: isNarrow ? 'row' : 'column',
        gap: 1.5,
        overflowX: isNarrow ? 'auto' : 'hidden',
      }}
    >
      {videos.map((video) => (
        <MediaLibraryVideoItem
          key={video.externalId}
          video={video}
          isNarrow={isNarrow}
          isDeletable={isDeletable(video)}
          deleteMessage={deleteMessage(video)}
          confirming={confirmId === video.externalId}
          deleting={deleting}
          deleteError={deleteError}
          onRequestDelete={() => setConfirmId(video.externalId)}
          onCancelDelete={cancelDelete}
          onConfirmDelete={() => void confirmDelete(video)}
          onEdit={() => onEdit(video)}
        />
      ))}
    </Box>
  );
}

/**
 * The sidebar's Media tab: the fund's video library — upload (drop or choose
 * files; adds to the library only), search, and the videos themselves. A video
 * nothing links to, or one that failed processing, can be deleted; a working
 * one that is still in use can't (the Video library page handles those). Ready videos drag onto a slide (or the "New slide" tile);
 * when the tab was opened from a slide's library button, clicking one attaches
 * it to that slide instead.
 */
export default function MediaLibraryTab({
  fundOpportunityId,
  backendService,
  isNarrow,
}: MediaLibraryTabProps) {
  const library = useVideoLibrary({ fundOpportunityId, backendService });
  const { pickTarget } = useEditorSidebar();
  const [editing, setEditing] = useState<VideoAsset | null>(null);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Typography variant="caption" color="text.secondary">
        {pickTarget
          ? `Click a video to use it on ${pickTarget.label}, or drag it onto any slide.`
          : 'Drag a video onto a slide or the New slide tile.'}
      </Typography>

      <MediaUploadZone
        uploading={library.uploading}
        uploadError={library.uploadError}
        onDismissError={library.dismissUploadError}
        onUpload={library.upload}
      />

      {library.isProcessing && (
        <Alert severity="info" sx={{ py: 0 }}>
          A video is still compressing — it can be used once it finishes.
        </Alert>
      )}

      <VideoLibrarySearchField value={library.search} onChange={library.setSearch} />

      {library.error && (
        <Typography variant="caption" color="error.main">
          {library.error}
        </Typography>
      )}

      <VideoList
        videos={library.videos}
        loading={library.loading}
        isNarrow={isNarrow}
        onRemove={(video) => library.remove(video.externalId, usageCount(video) > 0)}
        onEdit={setEditing}
      />

      <VideoMetadataEditDialog
        video={editing}
        onCancel={() => setEditing(null)}
        onSave={async (externalId: string, fields: VideoMetadata) => {
          await library.updateMetadata(externalId, fields);
          setEditing(null);
        }}
      />
    </Box>
  );
}
